/**
 * Чистая логика экрана «Календарь бизнеса» (ТЗ §14.1, §14.2).
 *
 * Отдельно от `CalendarScreen.tsx` по той же причине, по которой домен отделён от сервисов
 * на бэкенде (AGENTS.md, «Стиль»): здесь принимаются решения, цена ошибки в которых —
 * не кривая вёрстка, а бан клиента не той кнопкой и «провал» вместо закрытого салона
 * в плитке. Такое проверяется тестом без DOM, а не глазами на скриншоте.
 *
 * Ни одна функция здесь ничего не форматирует по локали и не обращается к сети: наружу
 * отдаются КЛЮЧИ i18n и голые значения, строку выбирает компонент через `t()`.
 */

import type { AppointmentStatus } from '@app/shared';
import type { CalendarItemDto, CalendarStatsDto } from '../../../api/cabinetTypes';
import { shiftDayKey, type DayKey } from '../../../domain/datetime';
import type { UiKey } from '../../../i18n/dict';

// ──────────────────────────────────────────────────────────────────────────────
// Кнопки под карточкой
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Четыре операции статусов. Имена совпадают с методами `CabinetApiClient`, но само
 * действие здесь НЕ вызывается: набор кнопок — решение о показе, а вызов — эффект, и
 * смешав их, мы получили бы модуль, который нельзя протестировать без API-клиента.
 */
export type CalendarAction = 'complete' | 'cancel' | 'noShow' | 'forgive';

export interface ActionSpec {
  readonly action: CalendarAction;
  readonly labelKey: UiKey;
  /** Вариант `SmallButton` — цвета прототипа: `b-ok`, `b-ghost`, `b-warn`, `b-again`. */
  readonly variant: 'ok' | 'ghost' | 'warn' | 'accent';
  /**
   * Тексты шторки подтверждения; `null` — действие выполняется сразу.
   *
   * Подтверждения нет ровно у одной кнопки — «Пришёл». Она ничего не рассылает и ничего
   * не запрещает, а лишний вопрос на самой частой операции дня приучает жать «Да»
   * не читая — и тогда вопрос перестаёт защищать и там, где он нужен.
   */
  readonly confirm: { readonly titleKey: UiKey; readonly bodyKey: UiKey } | null;
}

export const ACTION_SPECS: Readonly<Record<CalendarAction, ActionSpec>> = {
  complete: { action: 'complete', labelKey: 'aCame', variant: 'ok', confirm: null },
  /* Отмена шлёт клиенту уведомление (ТЗ §11.4) — отозвать его нечем. */
  cancel: {
    action: 'cancel',
    labelKey: 'aCancel',
    variant: 'ghost',
    confirm: { titleKey: 'confirmCancelT', bodyKey: 'confirmCancelB' },
  },
  /* Неявка запускает шкалу санкций (ТЗ §10.2): вторая у этого же бизнеса — локальный бан,
     третья суммарно — глобальный. Одна кнопка способна закрыть человеку запись на всей
     платформе на 90 дней, и спросить перед этим обязательно. */
  noShow: {
    action: 'noShow',
    labelKey: 'aNoShow',
    variant: 'warn',
    confirm: { titleKey: 'confirmNoShowT', bodyKey: 'confirmNoShowB' },
  },
  /* Forgive снимает баны и пересчитывает счётчик (ТЗ §10.4) — тоже не тап «на всякий». */
  forgive: {
    action: 'forgive',
    labelKey: 'aForgive',
    variant: 'accent',
    confirm: { titleKey: 'confirmForgiveT', bodyKey: 'confirmForgiveB' },
  },
};

const NO_ACTIONS: readonly ActionSpec[] = [];

/**
 * Набор кнопок под карточкой (ТЗ §14.2).
 *
 * У `COMPLETED` и `CANCELLED` кнопок нет вовсе. Это не «забыли добавить»: у завершённой
 * записи отмечать нечего, а отменённую нельзя ни завершить, ни объявить неявкой — бэкенд
 * такие переходы отвергает, и нарисованная кнопка обещала бы владельцу операцию, которая
 * в ответ покажет ошибку.
 */
export function actionsFor(status: AppointmentStatus): readonly ActionSpec[] {
  switch (status) {
    case 'CREATED':
    case 'CONFIRMED':
      return [ACTION_SPECS.complete, ACTION_SPECS.cancel, ACTION_SPECS.noShow];
    case 'NO_SHOW':
      return [ACTION_SPECS.forgive];
    case 'COMPLETED':
    case 'CANCELLED':
      return NO_ACTIONS;
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Бейдж статуса
// ──────────────────────────────────────────────────────────────────────────────

export type CalendarTone = 'new' | 'ok' | 'done' | 'cancelled' | 'noshow';

/**
 * Подпись бейджа. Отдельная от клиентской (`domain/appointments.ts`) намеренно: у бизнеса
 * свой словарь прототипа (`cNew`, `cOk`, …) и своё различение поздней отмены.
 *
 * Поздняя отмена — отдельная подпись, а не просто «Отменена»: санкций за неё нет (ТЗ §10.1),
 * но именно она объясняет владельцу число в плитке «Поздние отмены». Без пометки на карточке
 * плитка показывала бы счётчик, к которому в списке нечего отнести.
 */
export function calendarTagKey(item: Pick<CalendarItemDto, 'status' | 'isLateCancel'>): UiKey {
  switch (item.status) {
    case 'CREATED':
      return 'cNew';
    case 'CONFIRMED':
      return 'cOk';
    case 'COMPLETED':
      return 'cDone';
    case 'NO_SHOW':
      return 'cNoshow';
    case 'CANCELLED':
      return item.isLateCancel ? 'cLateCancel' : 'cCancelled';
  }
}

export function calendarTone(status: AppointmentStatus): CalendarTone {
  switch (status) {
    case 'CREATED':
      return 'new';
    case 'CONFIRMED':
      return 'ok';
    case 'COMPLETED':
      return 'done';
    case 'CANCELLED':
      return 'cancelled';
    case 'NO_SHOW':
      return 'noshow';
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Плитки статистики
// ──────────────────────────────────────────────────────────────────────────────

export type StatTone = 'accent' | 'green' | 'danger' | 'gold';

export interface StatTile {
  readonly labelKey: UiKey;
  readonly tone: StatTone;
  /**
   * Готовая надпись или `null` — «показателя не существует», рисуется прочерком (`stNone`).
   *
   * Именно `null`, а не строка `'—'`: подпись прочерка живёт в словаре, и вернув её отсюда,
   * мы завели бы видимый текст мимо i18n.
   */
  readonly text: string | null;
}

/**
 * Проценты приходят с бэкенда уже целыми (`Math.round` в `calendarService`), поэтому здесь
 * значение НЕ округляется повторно: второе округление скрыло бы, что сервер начал слать
 * дробь, и разошлось бы с числом, по которому владелец задаёт вопрос поддержке.
 */
function percentText(value: number | null): string | null {
  return value === null ? null : `${value}%`;
}

/**
 * Пять плиток прототипа: `stats3` (записи, загрузка, неявки) + `stats2` (поздние отмены,
 * освобождённые слоты).
 *
 * ГЛАВНОЕ здесь — `null` доходит до экрана как `null`. `loadPercent: null` — выходной,
 * `noShowPercent: null` — день ещё не дал ни одного исхода. Ноль на их месте утверждал бы
 * «работали и ничего не продали» и «неявок не было» — оба утверждения ложны, а владелец
 * принимает по ним решения (AGENTS.md, правило 7). При этом настоящий ноль (`0`) обязан
 * рисоваться нулём: «0%» у отработанного дня — реальный и важный факт.
 */
export function statTiles(stats: CalendarStatsDto): readonly StatTile[] {
  return [
    { labelKey: 'stBookings', tone: 'accent', text: String(stats.bookings) },
    { labelKey: 'stLoad', tone: 'green', text: percentText(stats.loadPercent) },
    { labelKey: 'stNoshow', tone: 'danger', text: percentText(stats.noShowPercent) },
    { labelKey: 'stLate', tone: 'gold', text: String(stats.lateCancels) },
    { labelKey: 'stFreed', tone: 'accent', text: String(stats.freedSlots) },
  ];
}

// ──────────────────────────────────────────────────────────────────────────────
// Переключение дня
// ──────────────────────────────────────────────────────────────────────────────

export interface DayNavigation {
  readonly prev: DayKey;
  readonly next: DayKey;
  readonly isToday: boolean;
  /** `calToday` / `calYesterday` / `calTomorrow`, либо `null` для дальних дней. */
  readonly relativeKey: UiKey | null;
}

/**
 * Соседние дни и подпись «Вчера / Сегодня / Завтра».
 *
 * Арифметика идёт по строкам `YYYY-MM-DD` через `shiftDayKey`, а не прибавлением 24 часов
 * к моменту: сутки не всегда длятся 24 часа, и в поясе с переводом стрелок «завтра» уехало
 * бы на день. `today` передаётся аргументом и считается в поясе ОРГАНИЗАЦИИ вызывающим —
 * взять его из часов телефона значило бы называть «сегодня» вчерашний день у владельца,
 * открывшего кабинет ташкентским утром из Европы (AGENTS.md, правило 1).
 */
export function dayNavigation(day: DayKey, today: DayKey): DayNavigation {
  const relativeKey: UiKey | null =
    day === today
      ? 'calToday'
      : day === shiftDayKey(today, -1)
        ? 'calYesterday'
        : day === shiftDayKey(today, 1)
          ? 'calTomorrow'
          : null;

  return {
    prev: shiftDayKey(day, -1),
    next: shiftDayKey(day, 1),
    isToday: day === today,
    relativeKey,
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Список записей
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Записи по времени начала, ранние сверху — день читается сверху вниз, как он идёт.
 *
 * Порядок восстанавливается явно, хотя сервер уже отдаёт нужный: экран не должен ломаться
 * от того, что кто-то поменяет `orderBy` в выборке. Сортировка по МОМЕНТУ (`Date.parse`),
 * а не по строке: ISO со смещением сравнивается как текст неверно — `+05:00` и `Z`
 * дают одинаковые часы при разных моментах.
 */
export function sortByStart(items: readonly CalendarItemDto[]): readonly CalendarItemDto[] {
  return [...items].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}
